import { memo } from "react";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { options } from "@/config/contentful";

type RichTextContent = unknown;

type RichTextProps = {
  content?: RichTextContent;
  json?: unknown;
  className?: string;
};

const portableTextComponents: PortableTextComponents = {
  block: {
    h1: ({ children }) => <h1>{children}</h1>,
    h2: ({ children }) => <h2>{children}</h2>,
    h3: ({ children }) => <h3>{children}</h3>,
    h4: ({ children }) => <h4>{children}</h4>,
    normal: ({ children }) => <p>{children}</p>,
    blockquote: ({ children }) => <blockquote>{children}</blockquote>,
  },
  marks: {
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    link: ({ children, value }) => (
      <a
        href={value?.href || "#"}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  },
  list: {
    bullet: ({ children }) => <ul>{children}</ul>,
    number: ({ children }) => <ol>{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
};

const isPortableText = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.every(
    (item) => item && typeof item === "object" && "_type" in item
  );

const RichText = memo(({ content, json, className }: RichTextProps) => {
  const value = content ?? json;

  if (!value) {
    return null;
  }

  if (isPortableText(value)) {
    return (
      <div className={`${className} rich-text`}>
        <PortableText value={value as any} components={portableTextComponents} />
      </div>
    );
  }

  const richTextJson =
    typeof value === "object" && value && "json" in (value as any)
      ? (value as any).json
      : value;

  if (typeof richTextJson === "string") {
    return <div className={className}>{richTextJson}</div>;
  }

  if (!richTextJson || typeof richTextJson !== "object") {
    return <div className={className}>{richTextJson ?? null}</div>;
  }

  return (
    <div className={className}>
      {documentToReactComponents(richTextJson as any, options)}
    </div>
  );
});

RichText.displayName = "RichText";

export default RichText;
